import ServicePageTemplate from './ServicePageTemplate';

const dataAnalyticsData = {
  metaTitle: 'Data Analytics Services Dubai | Business Intelligence Solutions UAE',
  metaDescription: 'Transform business data into actionable insights with Scalizap\'s data analytics and business intelligence solutions designed to improve decision-making and operational efficiency.',
  bannerGradient: 'from-indigo-600 via-indigo-700 to-purple-600',
  category: 'Data & Intelligence',
  bannerTitle: 'Transform Raw Data Into Strategic Business Intelligence',
  bannerSubtitle: 'Unlock the full power of your data with advanced analytics, machine learning, and real-time dashboards that drive confident, data-backed decisions.',
  icon: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  stats: [
    { value: '10x', label: 'Faster Insights' },
    { value: '40%', label: 'Cost Reduction' },
    { value: '3x', label: 'Revenue Growth' },
    { value: '99%', label: 'Data Accuracy' }
  ],
  overview: {
    title: 'End-to-End Data Analytics Solutions',
    description: 'From data collection and warehousing to predictive modeling and interactive dashboards, Scalizap delivers comprehensive analytics solutions that transform your raw data into a competitive strategic advantage. We make complex data accessible and actionable for every level of your organization.',
    highlights: [
      'Real-time data pipelines with sub-second latency',
      'Predictive models that anticipate market trends',
      'Interactive dashboards accessible on any device',
      'Seamless integration with your existing data sources',
      'GDPR and data privacy compliance built in'
    ]
  },
  deliverables: [
    'Data Strategy & Roadmap',
    'ETL Pipeline Development',
    'Custom Analytics Dashboard',
    'Predictive ML Models',
    'Data Warehouse Setup',
    'Automated Reporting System',
    'Training & Documentation'
  ],
  benefits: [
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>,
      title: 'Real-Time Data Visibility',
      description: 'Live dashboards give stakeholders instant access to KPIs, operational metrics, and business performance across all departments.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>,
      title: 'Predictive Analytics',
      description: 'Machine learning models forecast demand, identify churn risk, and surface opportunities before your competitors see them.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>,
      title: 'Unified Data Warehouse',
      description: 'We consolidate all your data silos into a single source of truth, eliminating inconsistencies and enabling cross-functional analysis.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
      title: 'Advanced Visualization',
      description: 'Beautiful, interactive charts and graphs that translate complex datasets into clear visual narratives anyone can understand.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
      title: 'Data Governance & Quality',
      description: 'Automated data quality checks, lineage tracking, and governance frameworks ensure your data is always reliable and compliant.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>,
      title: 'Self-Service Analytics',
      description: 'Empower non-technical users to explore data independently with intuitive tools, reducing dependency on the data team.'
    }
  ],
  process: [
    { step: '01', title: 'Data Assessment', description: 'We audit your existing data sources, quality, and infrastructure to identify gaps and opportunities.' },
    { step: '02', title: 'Architecture Design', description: 'We design a scalable data architecture including pipelines, warehouse schema, and visualization strategy.' },
    { step: '03', title: 'Build & Integrate', description: 'ETL pipelines, ML models, and dashboards are built and integrated with your existing business systems.' },
    { step: '04', title: 'Deploy & Optimize', description: 'We launch, monitor performance, and continuously refine models and dashboards based on your feedback.' }
  ],
  technologies: ['Python', 'R', 'Apache Spark', 'Apache Kafka', 'dbt', 'Snowflake', 'BigQuery', 'Redshift', 'Tableau', 'Power BI', 'Looker', 'TensorFlow', 'PyTorch', 'Scikit-learn', 'Airflow', 'AWS Glue'],
  cta: {
    title: 'Ready to Make Data Your Competitive Advantage?',
    subtitle: 'Stop guessing and start knowing. Let Scalizap build an analytics platform that turns your data into your most powerful business asset.'
  },
  faqs: [
    {
      question: 'What data sources can you integrate?',
      answer: 'We can integrate virtually any data source including databases (SQL/NoSQL), SaaS platforms (Salesforce, HubSpot, Shopify), APIs, spreadsheets, IoT sensors, and data streams. Our ETL pipelines are built to handle both structured and unstructured data at any scale.'
    },
    {
      question: 'How long does it take to set up a data analytics platform?',
      answer: 'A basic analytics setup with dashboards and reporting typically takes 4–8 weeks. A full enterprise data platform with machine learning models can take 3–6 months. We start delivering value early through iterative sprints so you see results before the full build is complete.'
    },
    {
      question: 'Do we need to have clean, organized data to get started?',
      answer: 'No — data quality issues are extremely common and we expect them. Our data engineering team handles data cleansing, deduplication, and standardization as part of the project. We turn messy raw data into reliable, analysis-ready datasets.'
    },
    {
      question: 'What machine learning capabilities do you offer?',
      answer: 'We build custom ML models for demand forecasting, customer churn prediction, sentiment analysis, anomaly detection, recommendation engines, computer vision, and more. We select the right algorithms based on your data and business problem.'
    },
    {
      question: 'Can your dashboards integrate with our existing BI tools?',
      answer: 'Yes. We work with all major BI platforms including Tableau, Power BI, Looker, and Metabase, and we can also build custom web-based dashboards. We connect these to your existing data warehouse or build a new one as needed.'
    },
    {
      question: 'How do you ensure data security and compliance?',
      answer: 'We implement role-based access control, end-to-end encryption, audit logging, and data anonymization where required. We design all analytics systems to comply with GDPR, CCPA, and industry-specific regulations such as HIPAA or PCI-DSS.'
    }
  ]
};

const DataAnalyticsPage = () => <ServicePageTemplate service={dataAnalyticsData} />;
export default DataAnalyticsPage;
